import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, CheckCircle, Key, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import BrandLogo from "@/components/layout/BrandLogo";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Welcome — 7-day magic-link landing page.
 * Admin-created users can arrive here two ways:
 *   1) `/w/<code>` — short link. We exchange the code for the JWT via
 *      `/auth/welcome/resolve/<code>` before proceeding.
 *   2) `/welcome?token=<jwt>` — legacy long-link (kept alive so links sent
 *      before the short-link rollout still work).
 * Once a JWT is in hand the page verifies it, lets the user set their
 * password, and signs them in on success (skipping MFA — the emailed link is
 * itself proof of inbox control).
 */
export default function Welcome() {
  const nav = useNavigate();
  const { code } = useParams();
  const [params] = useSearchParams();
  const [token, setToken] = useState(params.get("token"));
  const { completePasswordChange } = useAuth();

  const [state, setState] = useState("verifying"); // verifying | ready | invalid | done
  const [info, setInfo] = useState(null);
  const [errMsg, setErrMsg] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // If we arrived via `/w/<code>`, exchange it for the underlying JWT first.
    if (!token && code) {
      axios
        .get(`${API}/auth/welcome/resolve/${code}`)
        .then((r) => {
          setToken(r.data.token);
          setInfo({ email: r.data.email, name: r.data.name, role: r.data.role });
          setState("ready");
        })
        .catch((e) => {
          setState("invalid");
          setErrMsg(e?.response?.data?.detail || "This welcome link is no longer valid.");
        });
      return;
    }
    if (!token) {
      setState("invalid");
      setErrMsg("This welcome link is missing its token. Please use the link from your email.");
      return;
    }
    axios
      .get(`${API}/auth/welcome/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setInfo(r.data);
        setState("ready");
      })
      .catch((e) => {
        setState("invalid");
        setErrMsg(e?.response?.data?.detail || "This welcome link is no longer valid.");
      });
  }, [token, code]);

  const submit = async (e) => {
    e?.preventDefault();
    if (pw !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/auth/welcome/set-password`,
        { new_password: pw, confirm_password: confirm },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      completePasswordChange(r.data.token, r.data.user);
      setState("done");
      toast.success("Welcome to Inventya Capital.");
      setTimeout(() => nav("/"), 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not set your password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <div className="hidden lg:flex flex-1 relative overflow-hidden bg-slate-900 text-white">
        <div className="absolute inset-0 grid-backdrop opacity-40" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-4">
            <BrandLogo className="h-16 w-auto object-contain" forceLight />
            <div className="leading-tight border-l border-white/15 pl-4">
              <div className="font-display font-bold text-2xl">Inventya Capital</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 mt-1 font-semibold">Investor Portal</div>
            </div>
          </div>
          <div className="max-w-md">
            <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-400 mb-4 font-bold">Activate your account</div>
            <h1 className="font-display text-4xl lg:text-6xl font-bold leading-[1.02] tracking-tight">
              Choose a password.
              <span className="text-emerald-400"> Sign straight in.</span>
            </h1>
            <p className="mt-6 text-white/70 leading-relaxed text-lg">
              No temporary codes, no OTP dance. Your emailed link is proof enough — just pick a password and you&apos;re inside the deal room.
            </p>
          </div>
          <div className="text-xs text-white/40">© 2026 Inventya Capital. Access restricted to qualified investors.</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <BrandLogo className="h-16 w-auto object-contain" />
            <div><div className="font-display font-bold text-xl">Inventya Capital</div></div>
          </div>

          {state === "verifying" && (
            <div className="text-center py-16" data-testid="welcome-verifying">
              <div className="mx-auto h-10 w-10 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" />
              <p className="mt-6 text-sm text-muted-foreground">Verifying your invitation…</p>
            </div>
          )}

          {state === "invalid" && (
            <div data-testid="welcome-invalid">
              <div className="mx-auto h-12 w-12 rounded-full bg-red-100 dark:bg-red-950/40 grid place-items-center mb-4">
                <WarningCircle size={28} weight="fill" className="text-red-600" />
              </div>
              <h2 className="font-display text-3xl font-bold tracking-tight">Link no longer valid</h2>
              <p className="mt-3 text-muted-foreground text-sm leading-relaxed">{errMsg}</p>
              <Button
                onClick={() => nav("/signin")}
                data-testid="welcome-goto-signin-btn"
                className="mt-8 w-full h-11 bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
              >
                Go to sign in <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          )}

          {state === "ready" && info && (
            <div data-testid="welcome-form">
              <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3 font-bold">Welcome</div>
              <h2 className="font-display text-4xl font-bold tracking-tight">Hello, {info.name?.split(" ")[0] || "there"}</h2>
              <p className="mt-3 text-muted-foreground text-sm leading-relaxed">
                You&apos;ve been invited to the <b>Inventya Capital</b> investor portal as <b>{info.email}</b>. Choose a password to activate your account.
              </p>
              <form onSubmit={submit} className="mt-8 space-y-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">New password</Label>
                  <Input
                    type="password"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    className="h-11"
                    data-testid="welcome-password-input"
                    autoComplete="new-password"
                    required
                  />
                  <div className="text-[11px] text-muted-foreground mt-1">Min 8 chars, with upper, lower, number and symbol.</div>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Confirm password</Label>
                  <Input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="h-11"
                    data-testid="welcome-confirm-input"
                    autoComplete="new-password"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy || !pw || pw !== confirm}
                  data-testid="welcome-submit-btn"
                  className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
                >
                  <Key size={16} className="mr-2" />
                  {busy ? "Activating account…" : "Set password & sign in"}
                </Button>
              </form>
            </div>
          )}

          {state === "done" && (
            <div className="text-center py-12" data-testid="welcome-done">
              <div className="mx-auto h-12 w-12 rounded-full bg-emerald-100 dark:bg-emerald-900/40 grid place-items-center mb-4">
                <CheckCircle size={28} weight="fill" className="text-emerald-600" />
              </div>
              <h2 className="font-display text-3xl font-bold tracking-tight">You&apos;re in.</h2>
              <p className="mt-3 text-muted-foreground text-sm">Redirecting to the deal room…</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
